import styled from 'styled-components';
import Slide from '../containers/Slide';
import { motion, useInView } from "framer-motion"
import React, { useRef } from 'react';
import SectionHeader from '../widgets/SectionHeader';

const Container = styled.div.attrs({ className: "contact-slide" })`
    width: 75%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    box-sizing: border-box;
    padding: 0 0 6rem 0;
    gap: 2rem;

    h3 {
        color: var(--brand);
        font-size: 2.4rem;
        font-weight: normal;
    }

    p {
        box-sizing: border-box;
        font-size: 1.2rem;
        line-height: 1.8rem;
        color: var(--muted);
    }

    & > p {
        border-left: 4px solid var(--yellow);
        padding: 0 1rem;
    }

    #contact-container {
        width: 100%;
        display: flex;
        gap: 2rem;
        justify-content: stretch;
        align-items: stretch;
    
        #left-of-contact-container {
            width: 100%;
            border-radius: var(--radius-xl);
            overflow: hidden;
            border: 1px solid var(--border);
            background: var(--surface);
            box-shadow: var(--shadow-soft);
            min-height: 420px;

            iframe {
                width: 100%;
                height: 100%;
                border: 0;
                display: block;
            }

        }

        #right-of-contact-container {
            width: 35%;
            flex-shrink: 0;
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: var(--radius-xl);
            padding: 20px;
            box-shadow: var(--shadow-soft);
            display: grid;
            gap: 14px;
            height: fit-content;

            .contact-list {
                margin: 0;
                padding: 0;
                list-style: none;
                display: grid;
                gap: 10px;
            }

            .contact-item {
                display: flex;
                justify-content: space-between;
                gap: 12px;
                padding: 12px 12px;
                border-radius: 16px;
                border: 1px solid var(--border);
                background: 
            color-mix(in srgb, var(--surface-2) 70%, white);
                font-weight: 800;
            }

            form {
                display: grid;
                gap: 12px;

                .field {
                    display: grid;
                    gap: 7px;

                    label {
                        font-weight: 800;
                        font-size: 13px;
                        color: 
                    color-mix(in srgb, var(--text) 80%, var(--muted));
                    }

                    input, textarea {
                        width: 100%;
                        padding: 12px 14px;
                        border-radius: 14px;
                        border: 1px solid var(--border);
                        background: var(--surface);
                        font: inherit;
                        outline: none;
                        transition: box-shadow .12s ease, border-color .12s ease;
                    }
                }
            }

            .btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 12px 16px;
                border-radius: 999px;
                border: 1px solid var(--border);
                background: var(--surface);
                font-weight: 700;
                cursor: pointer;
                transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
                box-shadow: var(--shadow-soft);
                white-space: nowrap;
            }

            .btn.primary {
                background: linear-gradient(135deg, var(--brand), var(--brand-2));
                color: white;
                border-color: rgba(255, 255, 255, .18);
                box-shadow: 0 18px 45px rgba(14, 134, 204, .28);
            }
        }
    }

    @media screen and (max-width: 980px) {
        height: auto;

        
    }

    @media screen and (max-width: 1000px) {
        width: 100%;
        box-sizing: border-box;
        padding: 0 2rem 6rem 2rem;

        #contact-container {
            flex-direction: column;
            align-items: center;

            #left-of-contact-container {
                width: 100%;
                aspect-ratio: 2 / 1;
            }

            #right-of-contact-container {
                width: 100%;
            }
        }
    }
`

export default function ContactSlide() {

    /*
        The animation is conditional on the window's innerWidth (in coordination with the CSS media queries).
        If desktop, stagger the li's via the parent.
        If mobile, the parent stagger is discarded, and each li is responsible for its own animation.

        This was done to give that 'each item appears individually' feel.
    */

    const liAnim = {
        hidden: { opacity: 0, left: '100%' },
        visible: { opacity: 1, left: '0%' }
    }

    // One ref and useInView for each education item
    const liRef1 = useRef(null);
    const liInView1 = useInView(liRef1, { once: true });

    const liRef2 = useRef(null);
    const liInView2 = useInView(liRef2, { once: true });

    const liRef3 = useRef(null);
    const liInView3 = useInView(liRef3, { once: true });

    const liRef4 = useRef(null);
    const liInView4 = useInView(liRef4, { once: true });

    const liRef5 = useRef(null);
    const liInView5 = useInView(liRef5, { once: true });

    const liRef6 = useRef(null);
    const liInView6 = useInView(liRef6, { once: true });

    const liRefArr = [
        { ref: liRef1, inView: liInView1 },
        { ref: liRef2, inView: liInView2 },
        { ref: liRef3, inView: liInView3 },
        { ref: liRef4, inView: liInView4 },
        { ref: liRef5, inView: liInView5 },
        { ref: liRef6, inView: liInView6 }
    ];

    // One ref and useInView for each education item
    const ulRef = useRef(null);
    const ulInView = useInView(ulRef, { once: true })

    return (
        <Slide color="var(--light-yellow);" height="auto" freeHeightBreakpoint="980px">

            <Container>

                <SectionHeader title="Contact" id="contact-section" />

                <h3>
                    Get Your Free Estimate
                </h3>

                <p>
                    Tell us what you're building. We'll respond quickly with next steps and scheduling.
                </p>

                <div id='contact-container'>

                    <div id='left-of-contact-container'>

                        <iframe loading="lazy" referrerpolicy="no-referrer-when-downgrade"
                            src="https://www.google.com/maps?q=1020%20Tennessee%20Ave%20Fort%20Lauderdale%20FL%2033312&amp;output=embed"
                            title="Flooring Covering Services LLC - Map">
                        </iframe>

                    </div>

                    <div id="right-of-contact-container">
                        <div>
                            <h3>Flooring Covering Services LLC</h3>
                            <p>
                                1020 Tennessee Ave, Fort Lauderdale, FL 33312
                            </p>
                        </div>

                        <ul class="contact-list" aria-label="Business contact details">
                            <li class="contact-item">
                                <div>Phone</div>
                                <span><a href="tel:7326934595">(732) 693-4595</a></span>
                            </li>
                            <li class="contact-item">
                                <div>Email</div>
                                <span><a href="mailto:Oliveirajr1081@gmail.com">Oliveirajr1081@gmail.com</a></span>
                            </li>
                        </ul>

                        <form id="quoteForm">
                            <div class="field">
                                <label for="name">Name</label>
                                <input id="name" name="name" autocomplete="name" placeholder="Your name" required="" />
                            </div>

                            <div class="field">
                                <label for="phone">Phone</label>
                                <input id="phone" name="phone" autocomplete="tel" placeholder="(555) 555-5555" />
                            </div>

                            <div class="field">
                                <label for="message">Project details</label>
                                <textarea id="message" name="message" placeholder="What do you want done? Timeline? Materials?"></textarea>
                            </div>

                            <button class="btn primary" type="submit">Send Request</button>
                            <p>
                                This opens your email app with the message pre-filled.
                            </p>
                        </form>
                    </div>

                </div>

            </Container>

        </Slide>
    )
}